import React, { useState, useContext, useEffect, useRef } from 'react';

import { 
  Menu, 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Button, 
  Divider, 
  IconButton, 
  MenuItem,
  Snackbar,
  Alert,
  AlertTitle
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import NotificationItem from './NotificationItem';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import { useAuth } from '../../Context/AuthContext';
import { NotificationsContext } from '../../Context/NotificationsContexteProvider';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import * as actions from '../../Reducers/Actions';
import { getIcon } from './NotificationIcons';
import { useNavigate } from 'react-router-dom';

const NotificationsMenu = ({ anchorEl, open, onClose, setUnreadCount }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const { socket, user } = useAuth();
  const { notifications, dispatchNotifications } = useContext(NotificationsContext);
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const isActionMenuOpen = Boolean(actionMenuAnchorEl);

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupNotification, setPopupNotification] = useState(null);

  const lastTabFetchRef = useRef({ tab: 0, timestamp: 0 });
  const MIN_TAB_FETCH_INTERVAL = 10000; // 10 secondes minimum entre les requêtes

  const handleActionMenuClick = (event) => {
    setActionMenuAnchorEl(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
  };

  // Format date for popup
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Fermer le popup
  const handleClosePopup = () => {
    setShowPopup(false);
    setPopupNotification(null);
  };

  // Determine alert severity based on server-provided type
  const getAlertSeverity = (notification) => {
    if (!notification) return 'info';
    
    // Utiliser directement le type fourni par le serveur s'il existe
    if (notification.type) {
      // Valider que le type est l'un des types valides pour Material UI Alert
      if (['success', 'info', 'warning', 'error'].includes(notification.type)) {
        return notification.type;
      }
    }
    
    // Si pas de type ou type invalide, retour à la valeur par défaut
    return 'info';
  };

  useEffect(() => {
    if (socket && user?.id) {
      const getNotifications = () => {
        if (!socket.connected) return;
        socket.emit('getOldNotifications', { 
          userId: user.id,
          employeeId: user.id
        });
      };

      socket.on('getNotification', (data) => {
        if (!data) return;
        
        if (data.success && data.notifications && Array.isArray(data.notifications)) {
          const existingIds = new Set(notifications.map(n => n.id));
          const newNotifications = data.notifications.filter(n => !existingIds.has(n.id));
          
          if (newNotifications.length > 0) {
            dispatchNotifications({ 
              type: actions.ADD_NOTIFICATION, 
              payload: newNotifications 
            });
            
            // Vérifier les nouvelles notifications pour le popup
            checkForPopupNotifications(newNotifications);
          } else if (notifications.length === 0 && data.notifications.length > 0) {
            dispatchNotifications({ 
              type: 'SET_NOTIFICATIONS', 
              payload: data.notifications 
            });
            
            // Vérifier les notifications pour le popup
            checkForPopupNotifications(data.notifications);
          }
        }
      });

      socket.on('liveNewNotifications', (data) => {
        if (!data) return;

        // Simplifier la logique de conversion en tableau
        const notificationsToAdd = Array.isArray(data) ? data : (typeof data === 'object' ? [data] : []);
        
        if (notificationsToAdd.length > 0) {
          // Ajouter directement les notifications sans traitement complexe
          dispatchNotifications({ 
            type: actions.ADD_NOTIFICATION, 
            payload: notificationsToAdd
          });
          
          // Vérifier les nouvelles notifications pour le popup
          checkForPopupNotifications(notificationsToAdd);
        }
      });

      socket.on('deleteNotification', (response) => {
        if (response.success) {
          dispatchNotifications({
            type: actions.DELETE_NOTIFICATION,
            id: response.id
          });
          
          // Si c'est la notification actuellement affichée dans le popup, fermer le popup
          if (popupNotification && popupNotification.id === response.id) {
            setShowPopup(false);
            setPopupNotification(null);
          }
        }
      });

      socket.on('response', (response) => {
        if (response.success) {
          // Keep UI update if successful
        } else {
          // Refresh notifications if server update failed
          socket.emit('getOldNotifications', { userId: user.id,  employeeId: user.id });
        }
      });

      // Initial load
      getNotifications();

      return () => {
        socket.off('getNotification');
        socket.off('liveNewNotifications');
        socket.off('response');
        socket.off('deleteNotification');
        socket.off('auth_error');
        socket.off('error');
        socket.off('err');
      };
    }
  }, [socket, activeTab, user?.id]);

  // Check notifications that should appear in popup
  const checkForPopupNotifications = (notificationsList) => {
    // Ne pas afficher de popup si un est déjà ouvert
    if (showPopup) return;
    
    // Titles that will trigger popup
    const popupNotificationTitles = [
      'clock-out',
      'clock-in',
      'schedule validation',
      'attendance validation'
    ];
    
    // Filter only relevant notifications for popup
    const relevantNotifications = notificationsList.filter(notif => {
      if (!notif || !notif.title) return false;
      
      const lowerTitle = notif.title.toLowerCase();
      return popupNotificationTitles.some(title => lowerTitle.includes(title));
    });
    
    if (relevantNotifications.length > 0) {
      // Prendre la première notification non lue, ou la première si toutes sont lues
      const notifToShow = relevantNotifications.find(n => !n.isRead) || relevantNotifications[0];
      
      // Determine severity from server type
      const severity = getAlertSeverity(notifToShow);
      
      // Show notification via SnackBarContext
      setInfoAlert({
        severity: severity,
        message: notifToShow.message || ""
      });
      setOpenSnackBar(true);
      
      // Keep current popup notification to mark as read after display
      setPopupNotification(notifToShow);
      setShowPopup(true);
      
      // Marquer automatiquement comme lu après quelques secondes
      setTimeout(() => {
        if (socket && notifToShow && !notifToShow.isRead) {
          handleMarkAsRead(notifToShow);
        }
      }, 6000); // match SnackBar auto hide duration
    }
  };

  useEffect(() => {
    const unreadCount = notifications.filter(n => !n.isRead).length;
    setUnreadCount(unreadCount);
  }, [notifications, setUnreadCount]);

  const handleMarkAsRead = (notification) => {
    if (socket) {
      const notificationId = notification.id;
      dispatchNotifications({
        type: actions.MARK_SEEN_NOTIFICATION,
        id: notificationId
      });
      socket.emit('Seen', { 
        notificationId: notificationId,
        userId: user.id 
      });
      
      // Si c'est la notification actuellement affichée dans le popup, fermer le popup
      if (popupNotification && popupNotification.id === notificationId) {
        setShowPopup(false);
        setPopupNotification(null);
      }
    }
  };

  const makeAllNotifsSeen = () => {
    if (socket) {
      socket.emit('Seen', { "userId": user.id, "employeeId": user.id });
      dispatchNotifications({ type: actions.MARK_ALL_SEEN_NOTIFICATIONS });
      handleActionMenuClose();
      
      // Fermer le popup si ouvert
      if (showPopup) {
        setShowPopup(false);
        setPopupNotification(null);
      }
    }
  };

  const deleteAllNotifs = () => {
    if (socket) {
      socket.emit('deleteNotification', { "userId": user.id, "employeeId": user.id });
      dispatchNotifications({ type: actions.CLEAR_NOTIFICATIONS_LIST });
      handleActionMenuClose();
      
      // Fermer le popup si ouvert
      if (showPopup) {
        setShowPopup(false);
        setPopupNotification(null);
      }
    }
  };

  // Determine if a notification represents a quiz assignment
  const isAssignedQuizNotification = (n) => {
    if (!n) return false;
    const title = String(n.title || '').toLowerCase();
    const category = String(n.category || '').toLowerCase();
    const type = String(n.type || '').toLowerCase();
    // Heuristics: category is quiz OR type mentions quiz OR title mentions quiz + assignment
    return (
      category === 'quiz' ||
      type.includes('quiz') ||
      (title.includes('quiz') && (title.includes('assigned') || title.includes('assignment') || title.includes('assign')))
    );
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Ne pas refaire une requête si on a déjà récupéré les données pour cet onglet récemment
    const now = Date.now();
    const sameTab = lastTabFetchRef.current.tab === newValue;
    const recentFetch = now - lastTabFetchRef.current.timestamp < MIN_TAB_FETCH_INTERVAL;
    
    if (socket && user && (!sameTab || !recentFetch)) {
      lastTabFetchRef.current = { tab: newValue, timestamp: now };
      socket.emit('getOldNotifications', { userId: user.id, employeeId: user.id });
    }
  };

  const handleDelete = (notification) => {
    if (socket) {
      dispatchNotifications({
        type: actions.DELETE_NOTIFICATION,
        id: notification.id
      });
      socket.emit('deleteNotification', { notificationId: notification.id });
      
      // Si c'est la notification actuellement affichée dans le popup, fermer le popup
      if (popupNotification && popupNotification.id === notification.id) {
        setShowPopup(false);
        setPopupNotification(null);
      }
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === 0) return true;
    if (activeTab === 1) return !notification.isRead;
    if (activeTab === 2) return notification.isRead;
    return true;
  });

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: '90%',
            mt: 1.5,
          },
        }}
      >
        <Box p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Notifications</Typography>
            <IconButton
              onClick={handleActionMenuClick}
              size="small"
              sx={{ color: colors.primary[400] }}
            >
              <MoreVertIcon />
            </IconButton>
            
            <Menu
              anchorEl={actionMenuAnchorEl}
              open={isActionMenuOpen}
              onClose={handleActionMenuClose}
            >
              <MenuItem onClick={makeAllNotifsSeen}>
                Mark all as read
              </MenuItem>
              <MenuItem onClick={deleteAllNotifs}>
                Delete all
              </MenuItem>
            </Menu>
          </Box>

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="All" />
            <Tab label="Unread" />
            <Tab label="Read" />
          </Tabs>

          <Box>
            {filteredNotifications.length > 0 ? (
              filteredNotifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    onRead={() => handleMarkAsRead(notification)}
                    onDelete={() => handleDelete(notification)}
                    onClick={() => {
                      if (isAssignedQuizNotification(notification)) {
                        navigate('/quiz/assigned');
                        try { onClose?.(); } catch (_) {}
                      }
                    }}
                  />
                  {index < filteredNotifications.length - 1 && <Divider />}
                </React.Fragment>
              ))
            ) : (
              <Typography sx={{ textAlign: 'center', color: colors.grey[500], py: 2 }}>
                No notifications to display
              </Typography>
            )}
          </Box>
        </Box>
      </Menu>
      
    </>
  );
};

export default NotificationsMenu;